import React, { useCallback, useMemo } from "react";
import { IconButton } from "@xsolla/xui-button";
import { Select } from "@xsolla/xui-select";
import { ArrowLeft, ArrowRight } from "@xsolla/xui-icons-base";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { getMonthInLocale } from "./utils";
import type { CalendarHeaderProps } from "./types";

const months = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  monthDate,
  decreaseMonth,
  increaseMonth,
  changeYear,
  changeMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  locale,
  minDate,
  maxDate,
  contextMenuMaxHeight,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const handleChangeMonth = useCallback(
    (value: any) => {
      changeMonth(months.indexOf(value as string));
    },
    [changeMonth]
  );

  const handleChangeYear = useCallback(
    (value: any) => {
      changeYear(parseInt(value as string, 10));
    },
    [changeYear]
  );

  const monthOptions = useMemo(() => {
    return months.map((month, index) => ({
      label: getMonthInLocale(index, locale),
      value: month,
    }));
  }, [locale]);

  const yearOptions = useMemo(() => {
    const options = [];
    const yearStart = minDate ? minDate.getFullYear() : 1900;
    const yearEnd = maxDate
      ? maxDate.getFullYear()
      : new Date().getFullYear() + 100;
    for (let i = yearEnd; i >= yearStart; i--) {
      options.push({ value: i.toString(), label: i.toString() });
    }
    return options;
  }, [minDate, maxDate]);

  return (
    <Box
      testID={testID}
      width="100%"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      marginBottom={16}
      gap={8}
    >
      <IconButton
        size="xs"
        tone="mono"
        variant="tertiary"
        onPress={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        icon={<ArrowLeft size={16} />}
        aria-label="Previous month"
        themeMode={themeMode}
        themeProductContext={themeProductContext}
      />

      <Box flexDirection="row" flex={1} gap={4}>
        <Box flex={1.5}>
          <Select
            value={months[monthDate.getMonth()]}
            onChange={handleChangeMonth}
            options={monthOptions}
            size="sm"
            maxHeight={contextMenuMaxHeight}
            themeMode={themeMode}
            themeProductContext={themeProductContext}
          />
        </Box>
        <Box flex={1}>
          <Select
            value={monthDate.getFullYear().toString()}
            onChange={handleChangeYear}
            options={yearOptions}
            size="sm"
            maxHeight={contextMenuMaxHeight}
            themeMode={themeMode}
            themeProductContext={themeProductContext}
          />
        </Box>
      </Box>

      <IconButton
        size="xs"
        tone="mono"
        variant="tertiary"
        onPress={increaseMonth}
        disabled={nextMonthButtonDisabled}
        icon={<ArrowRight size={16} />}
        aria-label="Next month"
        themeMode={themeMode}
        themeProductContext={themeProductContext}
      />
    </Box>
  );
};
